import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Api from '../Network';
import { message } from 'antd';
import { getErrorMessage } from '../utils/getErrorMessage';

// Types
interface SalesRepresentativeFilters {
  name?: string;
  phone_number?: string;
  inventory_id?: string;
}

interface SalesRepresentativePaging {
  page: number;
  pageSize: number;
}

interface SalesRepresentativeSorting {
  [key: string]: 1 | -1;
}

interface SalesRepresentativeListState {
  filters: SalesRepresentativeFilters;
  paging: SalesRepresentativePaging;
  sorting: SalesRepresentativeSorting;
}

interface SalesRepresentativeQueryOptions {
  initialFilters?: SalesRepresentativeListState;
  baseURL?: string;
  useQueryOptions?: any;
  disableErrorHandling?: boolean;
}

export function useSalesRepresentatives(useListOptions?: SalesRepresentativeQueryOptions) {
  const [filters, setFilters] = useState<SalesRepresentativeListState>(
    useListOptions?.initialFilters || {
      filters: { name: '' },
      paging: { page: 1, pageSize: 20 },
      sorting: {},
    }
  );

  function setListFilters(path: string, value: any) {
    setFilters((filters) => {
      const newFilters = { ...filters } as any;
      const pathParts = path.split('.');
      let current = newFilters;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newFilters;
    });
  }

  useEffect(() => {
    setListFilters('paging', useListOptions?.initialFilters?.paging || { page: 1, pageSize: 20 });
  }, []); // Only run once on mount

  const queryResult = useQuery({
    queryKey: ['salesRepresentatives', filters],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const params = new URLSearchParams();
      params.append('page', filters.paging.page.toString());
      params.append('per-page', filters.paging.pageSize.toString());

      // Add show_all=true for sales-representatives page to show all drivers (active + inactive)
      params.append('filter[show_all]', 'true');

      Object.entries(filters.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (key === 'search' && value) {
            params.append('filter[name]', value.toString());
          } else {
            params.append(`filter[${key}]`, value.toString());
          }
        }
      });

      if (Object.keys(filters.sorting).length > 0) {
        const [field, direction] = Object.entries(filters.sorting)[0];
        params.append('sort', field);
        params.append('order', direction === 1 ? 'asc' : 'desc');
      }

      const url = `api/admin/drivers?${params.toString()}`;
      const response = await Api.get(url, {
        signal,
        baseURL: useListOptions?.baseURL,
      });

      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    ...useListOptions?.useQueryOptions,
  });

  useEffect(() => {
    if (queryResult.error && !useListOptions?.disableErrorHandling) {
      message.error(getErrorMessage(queryResult.error, 'Failed to load sales representatives'));
    }
  }, [queryResult.error, useListOptions?.disableErrorHandling]);

  return {
    filters,
    setListFilters,
    ...queryResult,
  };
}
